
  import {
    defineComponent,
    ref,
    onMounted,
    onBeforeUnmount,
    computed,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { useAbility } from '@casl/vue';
  import { useI18n } from 'vue-i18n/index';
  import { Actions } from '@/store/enums/StoreEnums';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';

  export default defineComponent({
    name: 'blogs-listing',
    components: {
      Datatable,
    },
    setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (key) => (te(key) ? t(key) : key);

      const loading = ref(false);

      const tableHeader = ref([
        {
          name: translate('actions'),
          key: 'actions',
        },
        { name: translate('ID'), key: 'id', sortable: true },
        { name: translate('Image'), key: 'imageUrl', sortable: true },
        { name: translate('titleEn'), key: 'titleEn', sortable: true },
        { name: translate('titleAr'), key: 'titleAr', sortable: true },
        { name: translate('authorName'), key: 'authorName', sortable: true },
        { name: translate('homepage'), key: 'isHomepage' },
        { name: translate('publishedAt'), key: 'publishedAt', sortable: true },
        { name: translate('publicity'), key: 'status', sortable: true },
        { name: translate('status'), key: 'isActive', sortable: true },
      ]);

      const fetchBlogs = async () => {
        loading.value = true;
        await store.dispatch(Actions.GET_BLOGS);
        loading.value = false;
      };

      const editBlog = (id: number) => {
        router.push({ name: 'blogs-editing', params: { id } });
      };

      const goTo = (pageName: string) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val: number) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_BLOG_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_BLOGS);
        loading.value = false;
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('blogs'), [
          translate('subscriptions'),
        ]);
        await fetchBlogs();
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_BLOGS_STORE);
      });
      const updateStatus = async (blog) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_BLOG, {
          id: blog.id,
          data: { status: blog.status },
        });
        loading.value = false;

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];
        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_STATUS'),
            icon: 'success',
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(async () => {
            fetchBlogs();
          });
        }
      };

      const editSeo = async (id) => {
        router.push({
          name: 'seo-configuration',
          params: { entityType: 'blogs', entityId: id },
        });
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
      };

      const toggleBlogStatus = async (blog) => {
        const deletedMessage = blog.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });
        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_BLOG, blog.id);
          await store.dispatch(Actions.GET_BLOGS);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = blog.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      return {
        tableData: computed(() => store.getters.blogsList),
        totalItems: computed(() => store.getters.blogsListCount),
        tableHeader,
        loading,
        toggleBlogStatus,
        translate,
        editBlog,
        goTo,
        pageChanged,
        can,
        updateStatus,
        editSeo,
      };
    },
  });
